import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';

import { TradeActivityOption, TradePlace, TradeSettingsView, useTradeApi } from '../../../../common/hooks/use-trade-api.hook';
import { activityLabel } from '../trade-format';
import { TradeOriginInput } from './TradeOriginInput';

const GROUPS: Array<TradeActivityOption['group']> = ['fitters', 'specifiers', 'builders'];

/** Where to prospect from, how far, which trades — and the trade offer every introduction quotes. */
export const TradeSettingsSheet = ({
    open,
    view,
    onClose,
    onSaved,
}: {
    open: boolean;
    view: TradeSettingsView | null;
    onClose: () => void;
    onSaved: (view: TradeSettingsView, refresh: boolean) => void;
}) => {
    const { t } = useTranslation();
    const api = useTradeApi();
    const s = view?.settings;
    const [address, setAddress] = useState('');
    // The suggestion picked for `address`, with its position: saved as a point, no geocoding.
    const [place, setPlace] = useState<TradePlace | null>(null);
    const [radius, setRadius] = useState(25);
    const [activities, setActivities] = useState<string[]>([]);
    const [weekly, setWeekly] = useState(true);
    const [soleTraders, setSoleTraders] = useState(true);
    const [offer, setOffer] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !s) return;
        setAddress('');
        setPlace(null);
        setRadius(s.radiusKm);
        setActivities(s.activities);
        setWeekly(s.refresh === 'weekly');
        setSoleTraders(s.includeSoleTraders);
        setOffer(s.tradeOffer || '');
    }, [open, s]);

    const byGroup = useMemo(() => {
        const out: Record<string, TradeActivityOption[]> = {};
        for (const a of view?.activities ?? []) (out[a.group] ||= []).push(a);
        return out;
    }, [view]);

    const toggle = (code: string, on: boolean) =>
        setActivities((list) => (on ? Array.from(new Set([...list, code])) : list.filter((c) => c !== code)));

    const save = async (refreshAfter: boolean) => {
        if (!activities.length) {
            toast.error(t('TRADE.SETTINGS.NEED_ACTIVITY', 'Choose at least one trade.'));
            return;
        }
        setSaving(true);
        try {
            const next = await api.updateSettings({
                ...(place
                    ? { origin: { lat: place.lat, lng: place.lng, label: place.label } }
                    : address.trim() ? { address: address.trim() } : {}),
                radiusKm: radius,
                activities,
                refresh: weekly ? 'weekly' : 'off',
                includeSoleTraders: soleTraders,
                tradeOffer: offer,
            });
            onSaved(next, refreshAfter);
        } catch (e: any) {
            toast.error(e?.body?.code === 'ADDRESS_NOT_FOUND'
                ? t('TRADE.SETTINGS.ADDRESS_NOT_FOUND', 'That address could not be found on the map. Try a postcode and town.')
                : t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        } finally {
            setSaving(false);
        }
    };

    const pickStore = async (storeId: string) => {
        setSaving(true);
        try {
            onSaved(await api.updateSettings({ storeId }), false);
        } catch {
            toast.error(t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(o) => !o && !saving && onClose()}>
            <SheetContent className="w-full gap-0 overflow-y-auto p-0 data-[side=right]:sm:max-w-[520px]" data-testid="trade-settings-sheet">
                <SheetHeader className="border-b p-6 pe-14">
                    <SheetTitle>{t('TRADE.SETTINGS.TITLE', 'Prospecting settings')}</SheetTitle>
                    <SheetDescription>{t('TRADE.SETTINGS.SUBTITLE', 'Where you sell from, how far you deliver, and which trades you sell to.')}</SheetDescription>
                </SheetHeader>
                <div className="flex flex-col gap-6 p-6">
                    <FieldGroup>
                        <Field>
                            <FieldLabel htmlFor="trade-origin">{t('TRADE.SETTINGS.ORIGIN', 'Where you are')}</FieldLabel>
                            {s?.origin && (
                                <p className="flex items-center gap-1.5 text-sm"><MapPin className="size-4 text-muted-foreground" />{s.origin.label}</p>
                            )}
                            <TradeOriginInput
                                id="trade-origin"
                                value={address}
                                onValueChange={(text) => {
                                    setAddress(text);
                                    setPlace(null);
                                }}
                                onPick={setPlace}
                                disabled={saving}
                            />
                            <FieldDescription>{t('TRADE.SETTINGS.ORIGIN_HINT', 'Leave empty to keep the current place.')}</FieldDescription>
                            {!!view?.stores.length && (
                                <div className="flex flex-wrap gap-2">
                                    {view.stores.map((store) => (
                                        <Button key={store.id} variant="outline" size="sm" disabled={saving} onClick={() => pickStore(store.id)}>
                                            <MapPin data-icon="inline-start" />
                                            {[store.name, store.city].filter(Boolean).join(', ')}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <div className="flex items-center justify-between">
                                <FieldLabel>{t('TRADE.SETTINGS.RADIUS', 'Distance')}</FieldLabel>
                                <span className="text-sm tabular-nums" data-testid="trade-radius-value">{t('TRADE.SETTINGS.RADIUS_VALUE', '{{km}} km', { km: radius })}</span>
                            </div>
                            <Slider
                                min={1}
                                max={50}
                                step={1}
                                value={[radius]}
                                onValueChange={(v) => setRadius(v[0])}
                                getValueText={(v) => t('TRADE.SETTINGS.RADIUS_VALUE', '{{km}} km', { km: v })}
                                aria-label={t('TRADE.SETTINGS.RADIUS', 'Distance')}
                            />
                            <FieldDescription>{t('TRADE.SETTINGS.RADIUS_HINT', 'Up to 50 km — the registry’s limit. Your delivery area is a good guide.')}</FieldDescription>
                        </Field>
                    </FieldGroup>

                    <Separator />

                    <div className="flex flex-col gap-4">
                        <div>
                            <h3 className="text-sm font-medium">{t('TRADE.SETTINGS.ACTIVITIES', 'Trades')}</h3>
                            <p className="text-xs text-muted-foreground">{t('TRADE.SETTINGS.ACTIVITIES_HINT', 'By their official activity code.')}</p>
                        </div>
                        {GROUPS.filter((g) => byGroup[g]?.length).map((g) => (
                            <div key={g} className="flex flex-col gap-2">
                                <p className="text-xs font-medium text-muted-foreground">{t(`TRADE.GROUP.${g.toUpperCase()}`, g)}</p>
                                {byGroup[g].map((a) => (
                                    <Label key={a.code} className="flex items-start gap-3 font-normal">
                                        <Checkbox
                                            checked={activities.includes(a.code)}
                                            onCheckedChange={(v) => toggle(a.code, v === true)}
                                            className="mt-0.5"
                                            data-testid={`trade-activity-${a.code}`}
                                        />
                                        <span className="flex flex-col">
                                            <span className="text-sm">{activityLabel(a.code, t, a.labelEn)}</span>
                                            <span className="text-xs text-muted-foreground">{a.code} · {a.labelFr}</span>
                                        </span>
                                    </Label>
                                ))}
                            </div>
                        ))}
                    </div>

                    <Separator />

                    <FieldGroup>
                        <Field orientation="horizontal">
                            <div className="flex flex-col gap-0.5">
                                <FieldLabel htmlFor="trade-sole">{t('TRADE.SETTINGS.SOLE_TRADERS', 'Include sole traders')}</FieldLabel>
                                <FieldDescription>{t('TRADE.SETTINGS.SOLE_TRADERS_HINT', 'Most tilers in France work alone. Turn off to see only firms with a team.')}</FieldDescription>
                            </div>
                            <Switch id="trade-sole" checked={soleTraders} onCheckedChange={setSoleTraders} />
                        </Field>
                        <Field orientation="horizontal">
                            <div className="flex flex-col gap-0.5">
                                <FieldLabel htmlFor="trade-weekly">{t('TRADE.SETTINGS.WEEKLY', 'Look again every week')}</FieldLabel>
                                <FieldDescription>{t('TRADE.SETTINGS.WEEKLY_HINT', 'New businesses appear every week. We tell you when there are new ones.')}</FieldDescription>
                            </div>
                            <Switch id="trade-weekly" checked={weekly} onCheckedChange={setWeekly} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="trade-offer">{t('TRADE.SETTINGS.OFFER', 'Your trade offer')}</FieldLabel>
                            <Input
                                id="trade-offer"
                                value={offer}
                                maxLength={300}
                                onChange={(e) => setOffer(e.target.value)}
                                placeholder={t('TRADE.SETTINGS.OFFER_PLACEHOLDER', 'e.g. a trade account with delivery to site')}
                            />
                            <FieldDescription>{t('TRADE.SETTINGS.OFFER_HINT', 'Introductions quote it word for word. Without it they leave a place for you to fill — we never invent a discount.')}</FieldDescription>
                        </Field>
                    </FieldGroup>

                    <p className="text-xs text-muted-foreground">
                        {t('TRADE.SETTINGS.COUNTRY', 'France for now: professionals come from the public company register (SIRENE). Other countries’ registries will follow.')}
                    </p>
                </div>
                <SheetFooter className="border-t p-6 sm:flex-row sm:justify-end">
                    <Button variant="outline" onClick={() => save(false)} disabled={saving}>
                        {t('TRADE.SETTINGS.SAVE', 'Save')}
                    </Button>
                    <Button onClick={() => save(true)} disabled={saving} data-testid="trade-settings-save-refresh">
                        {saving && <Spinner data-icon="inline-start" />}
                        {t('TRADE.SETTINGS.SAVE_REFRESH', 'Save and refresh now')}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default TradeSettingsSheet;
